// consentScripts.js
// Conditionally injects third-party scripts based on the user's cookie consent.
//
// ──────────────────────────────────────────────
// IMPORTANT – replace the placeholder IDs below
// with your real tracking / pixel IDs.
// ──────────────────────────────────────────────

/**
 * Injects or removes third-party scripts depending on the consent preferences.
 *
 * @param {{ necessary: boolean, analytics: boolean, marketing: boolean }} prefs
 */
export function applyConsentScripts(prefs) {
  // ── Analytics (Google Analytics) ──────────────────
  if (prefs.analytics) {
    injectGoogleAnalytics();
  } else {
    removeScriptById('ga-script');
    removeScriptById('ga-init');
    // Revoke GA cookies
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
      });
    }
  }

  // ── Marketing (Conversion tracking for Instagram / TikTok ad campaigns) ──
  if (prefs.marketing) {
    injectMarketingScripts();
  } else {
    // TikTok pixel removal disabled — pixel not configured yet
    // removeScriptById('tiktok-pixel');
    removeScriptById('meta-pixel');
    if (window.fbq) {
      window.fbq('consent', 'revoke');
    }
    removeScriptById('google-ads');
    removeScriptById('google-ads-init');
    // Revoke Google Ads (ad_storage) when marketing denied
    if (window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
      });
    }
  }
}

/* ────────────────────────────────────────────────────
   Google Analytics 4
   Replace 'G-XXXXXXXXXX' with your actual GA4 Measurement ID.
   ──────────────────────────────────────────────────── */
function injectGoogleAnalytics() {
  if (document.getElementById('ga-script')) return; // already loaded

  const GA_ID = 'G-EHY9WW8QSG'; // ← your GA4 ID

  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  const init = document.createElement('script');
  init.id = 'ga-init';
  init.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){ dataLayer.push(arguments); }
    gtag('js', new Date());
    gtag('consent', 'update', { analytics_storage: 'granted' });
    gtag('config', '${GA_ID}', { anonymize_ip: true });
  `;
  document.head.appendChild(init);
}

/* ────────────────────────────────────────────────────
   Conversion-tracking pixels (TikTok Pixel & Meta Pixel)
   These track visitors who arrive on your site via paid
   ad campaigns on TikTok / Instagram so you can measure
   campaign effectiveness. They do NOT display ads on your site.
   Replace the placeholder IDs with your real pixel IDs.
   ──────────────────────────────────────────────────── */
function injectMarketingScripts() {
  // TikTok pixel injection commented out — not configured yet
  // injectTikTokPixel();
  injectMetaPixel();
  injectGoogleAds();
}

function injectTikTokPixel() {
  // TikTok pixel injection disabled — not configured yet.
  return;
}

function injectMetaPixel() {
  if (document.getElementById('meta-pixel')) return; // already loaded

  const META_PIXEL_ID = '2061098261448374';

  // Meta's official fbq stub + loader (adapted from their snippet for dynamic injection)
  const init = document.createElement('script');
  init.id = 'meta-pixel-init';
  init.textContent = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window,document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('consent', 'grant');
    fbq('init', '${META_PIXEL_ID}');
    fbq('track', 'PageView');
  `;
  init.id = 'meta-pixel';
  document.head.appendChild(init);
}

/* ────────────────────────────────────────────────────
   Google Ads conversion tracking
   Replace 'AW-XXXXXXXXX' and the conversion label with
   your real Google Ads conversion ID and label.
   Provides `reportGoogleConversion()` to fire conversion events.
   ──────────────────────────────────────────────────── */
function injectGoogleAds() {
  if (document.getElementById('google-ads') || document.getElementById('google-ads-init')) return;

  const GOOGLE_ADS_ID = 'AW-16693559902'; // ← your Google Ads Conversion ID
  // If gtag already exists (e.g., GA4 loaded), just update consent and configure
  if (window.gtag) {
    window.gtag('consent', 'update', { ad_storage: 'granted' });
    window.gtag('config', GOOGLE_ADS_ID);
    return;
  }

  const script = document.createElement('script');
  script.id = 'google-ads';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(script);

  const init = document.createElement('script');
  init.id = 'google-ads-init';
  init.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){ dataLayer.push(arguments); }
    gtag('js', new Date());
    gtag('consent', 'update', { ad_storage: 'granted' });
    gtag('config', '${GOOGLE_ADS_ID}');
  `;
  document.head.appendChild(init);
}

/**
 * Report a Google Ads conversion.
 * @param {{ value?: number, currency?: string, transaction_id?: string }} opts
 */
export function reportGoogleConversion(opts = {}) {
  const GOOGLE_ADS_ID = 'AW-16693559902'; // ← your Google Ads Conversion ID
  const CONVERSION_LABEL = 'XXXXXXXXXXXXXXX'; // ← your conversion label

  if (!window.gtag) {
    console.warn('gtag not available — conversion not sent');
    return;
  }

  const payload = {};
  if (opts.value != null) payload.value = opts.value;
  if (opts.currency) payload.currency = opts.currency;
  if (opts.transaction_id) payload.transaction_id = opts.transaction_id;
  payload.send_to = `${GOOGLE_ADS_ID}/${CONVERSION_LABEL}`;

  window.gtag('event', 'conversion', payload);
}

/**
 * Report a Meta Pixel standard event.
 * Only fires if the pixel is loaded (i.e. marketing consent was granted).
 * @param {string} eventName  e.g. 'ViewContent', 'AddToCart', 'InitiateCheckout', 'Purchase'
 * @param {object} params     event-specific parameters
 */
export function reportMetaEvent(eventName, params = {}) {
  if (!window.fbq) return;
  try {
    window.fbq('track', eventName, params);
  } catch (e) {
    // ignore
  }
}

/* ── Helpers ── */
function removeScriptById(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}
